import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { Radio } from '@mui/icons-material';
import { figma } from '../theme/figmaTokens';
import { formatStationMenuLabel } from '../utils/stationDisplay';

interface StationRow {
  id: number;
  name?: string;
  stationName?: string;
  callLetters: string;
  market: string;
}

interface StationSwitcherProps {
  onStationChange: (station: StationRow) => void;
}

const onBar = {
  label: 'rgba(255,255,255,0.92)',
  icon: '#999999',
  border: 'rgba(255,255,255,0.25)',
  borderHover: 'rgba(255,255,255,0.4)',
};

const StationSwitcher: React.FC<StationSwitcherProps> = ({ onStationChange }) => {
  const [stations, setStations] = useState<StationRow[]>([]);
  const [selectedStation, setSelectedStation] = useState<StationRow | null>(null);

  useEffect(() => {
    const stationsData = localStorage.getItem('userStations');
    const selectedStationData = localStorage.getItem('selectedStation');

    if (stationsData) {
      try {
        setStations(JSON.parse(stationsData));
      } catch (error) {
        console.error('Error parsing stations from localStorage:', error);
      }
    }

    if (selectedStationData) {
      try {
        setSelectedStation(JSON.parse(selectedStationData));
      } catch (error) {
        console.error('Error parsing selected station from localStorage:', error);
      }
    }
  }, []);

  const handleStationChange = (event: any) => {
    const stationId = event.target.value;
    const newStation = stations.find((station) => station.id === stationId);

    if (newStation) {
      localStorage.setItem('selectedStation', JSON.stringify(newStation));
      setSelectedStation(newStation);
      onStationChange(newStation);
    }
  };

  if (stations.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Radio sx={{ color: onBar.icon }} />
      <FormControl size="small" sx={{ minWidth: 240 }}>
        <Select
          value={selectedStation?.id || ''}
          onChange={handleStationChange}
          displayEmpty
          renderValue={(value) => {
            if (!value) {
              return (
                <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                  Select Station
                </Typography>
              );
            }
            const station = stations.find((s) => s.id === value) || selectedStation;
            return station ? formatStationMenuLabel(station) : '';
          }}
          sx={{
            color: onBar.label,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: onBar.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: onBar.borderHover },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: figma.cyan },
            '& .MuiSvgIcon-root': { color: onBar.icon },
          }}
        >
          {stations.map((station) => (
            <MenuItem key={station.id} value={station.id}>
              {formatStationMenuLabel(station)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default StationSwitcher;
